import { motion, useReducedMotion } from "framer-motion";
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import {
  useDraggable,
  useDroppable,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";

import type { FilterType } from "@/components/@types";
import {
  DASHBOARD_CANVAS_DROP_ID,
  isCatalogDragId,
  useReportsViewAssembleDndRegistration,
  type ReportsViewAssemblyDragSession,
} from "@/app/app/_/views/ReportsView/ReportsViewAssembleDndContext";
import {
  widgetUsesFlushContent,
  addWidgetToDefinition,
  definitionForActiveReportGroup,
  persistedWidgets,
  removeWidgetFromDefinition,
  updateActiveReportGroupLayouts,
  updateWidgetInDefinition,
  type DashboardGridCard,
  type ReportLayoutDefinition,
  type ReportLayoutWidget,
  widgetDisplayTitle,
  mergeWidgetDefaultFilters,
  resolveLayoutEntityIdentifiers,
  clampCardColSpan,
  fitCardsToGrid,
  getWidgetColSpanConstraints,
  reorderGhostCard,
  askedOfWidget,
  sourceByName,
} from "@/app/app/_/views/ReportsView/ReportsView.utils";
import {
  ReportsViewWidget,
  propsSayTheSame,
} from "@/app/app/_/views/ReportsView/ReportsViewWidget/ReportsViewWidget";
import { ReportsViewWidgetPrompt } from "@/app/app/_/views/ReportsView/ReportsViewWidget/ReportsViewWidgetPrompt";
import { ReportsViewWidgetQuestion } from "@/app/app/_/views/ReportsView/ReportsViewWidget/ReportsViewWidgetQuestion";
import { useReportSourceCatalog } from "@/app/app/_/views/ReportsView/reportFilterDefinitions.hooks";
import {
  ReportsViewCard,
  type ReportsViewCardProps,
} from "@/app/app/_/views/ReportsView/ReportsViewCard";
import { useLayoutColors } from "@/hooks/useLayoutColors";
import {
  useReportsViewWidgetHeaderFilters,
  useReportsViewWidgetFilterDefinitions,
} from "@/app/app/_/views/ReportsView/ReportsViewWidgetHeaderFilters.hooks";
import {
  ReportsViewWidgetFilterOverridesProvider,
  useReportsViewWidgetFilterOverrides,
} from "@/app/app/_/views/ReportsView/ReportsViewWidgetFilterOverridesContext";
import { useReportDefinitionEditor } from "@/app/app/_/views/ReportsView/useReportDefinitionEditor";
import { useReportsViewReportLayoutContext } from "@/app/app/_/views/ReportsView/ReportsViewReportLayoutContext";
import { State } from "@/state";
import type { BreakpointKey } from "@/state/@types";
import {
  getCardGridStyle,
  REPORTS_CARD_HEIGHT_PX,
  dragCardToGridCell,
  ensureGridPlacement,
  gridCellFromPointer,
  gridColumnCountForMode,
  reportsLayoutKeyForMode,
  resizeCardsInGrid,
  sameDashboardCardOrder,
  sortCardsByGridPosition,
  usesGridPlacementForMode,
  type ReportsGridMode,
} from "@/app/app/_/views/ReportsView/reportsGridLayout";
import { useReportsGridMode } from "@/app/app/_/views/ReportsView/ReportsView.hooks";
import { ReportsViewWidgetResizeHandle } from "@/app/app/_/views/ReportsView/ReportsViewWidgetResizeHandle";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";
import { Skeleton } from "@/components/ui/skeleton";

// ── Sortable card wrapper ─────────────────────────────────────────────────

type ResizeSide = "left" | "right" | "top" | "bottom";
type ResizeCursors = Partial<Record<ResizeSide, string | null>>;

/** Invisible spacer — the drag overlay already shows the moving card. */
function ReportsViewDragPlaceholder({ height }: { height?: number | null }) {
  return (
    <div
      aria-hidden
      className="w-full min-h-(--min-height) pointer-events-none"
      style={
        {
          "--min-height":
            (height ?? 120) <= 1 && (height ?? 120) !== 0
              ? `${(height ?? 120) * 100}%`
              : `${height ?? 120}px`,
        } as CSSProperties
      }
    />
  );
}

/**
 * A report's own tiles are raised off the page: each one paints the main
 * surface over the recessed canvas behind it, bordered and lifted by the
 * faintest shadow so it reads as a card rather than a tile of the page. The
 * same card is used by a shared report link, which keeps its own recessed
 * shade, so this is set here rather than on the card.
 */
/** A card on the canvas: edged and lifted, on the canvas's own surface. */
const REPORT_CARD_SURFACE_CLASS = "border border-solid border-border shadow-xs";

function useReportCardSurfaceStyle() {
  const colors = useLayoutColors();
  return useMemo(
    () =>
      ({
        "--report-card-surface": colors.main,
        "--report-card-surface-hover": colors.tableRowHover,
      }) as CSSProperties,
    [colors.main, colors.tableRowHover],
  );
}

export function ReportsViewSortableCard({
  id,
  gridStyle,
  arrangement,
  columnStep,
  rowStep,
  gridPlacementEnabled,
  isResizing,
  resizeCursors,
  onResizeGrid,
  onResizeGridEnd,
  cardProps,
  children,
}: {
  id: string;
  gridStyle: { gridColumn: string; gridRow: string; order?: number };
  /**
   * Which arrangement of this card this is. It travels when this changes and
   * at no other time - see the note on the ``motion.div`` below.
   */
  arrangement: number;
  columnStep: number;
  rowStep: number;
  gridPlacementEnabled: boolean;
  /** A resize is a pointer, and a card under one keeps up with it rather than springing after it. */
  isResizing: boolean;
  /** The cursor for each edge, saying which ways it can go; none, no handle. */
  resizeCursors?: ResizeCursors;
  onResizeGrid: (
    side: "left" | "right" | "top" | "bottom",
    delta: number,
  ) => void;
  onResizeGridEnd?: () => void;
  cardProps: Omit<ReportsViewCardProps, "children">;
  children: React.ReactNode;
}) {
  // Picked up by its header, and - on the stacked page, where a card is put
  // before whichever one it is dropped on - somewhere to drop another. The
  // grid places a card by the pointer, so there it is nothing to drop on, and
  // a pointer passing over it is nothing it has to hear about. (Sorted, every
  // card heard about every move of the pointer, and drew itself again.)
  const {
    attributes,
    listeners,
    setNodeRef: setDraggableRef,
    isDragging,
  } = useDraggable({ id });
  const { setNodeRef: setDroppableRef } = useDroppable({
    id,
    disabled: gridPlacementEnabled,
  });
  const setNodeRef = useCallback(
    (node: HTMLElement | null) => {
      setDraggableRef(node);
      setDroppableRef(node);
    },
    [setDraggableRef, setDroppableRef],
  );
  const surfaceStyle = useReportCardSurfaceStyle();
  const prefersLessMotion = useReducedMotion();
  const isStill = !isResizing && !isDragging && !prefersLessMotion;

  const assemble = {
    ...cardProps.assemble,
    headerDragProps: { ...attributes, ...listeners },
  };

  return (
    // A card sits where the grid puts it, and travels when the grid moves it:
    // a rearrange reads as the cards deciding where to go rather than the page
    // jumping. Only where it is is animated - ``position`` rather than the
    // whole layout - because a card that changed size on the way would have
    // the chart inside it measuring and redrawing at every frame of the
    // journey, which is why this was taken out before. Moving is a transform,
    // and a transform is nothing a chart has to hear about.
    //
    // A card being dragged is where the pointer is, not where a spring thinks
    // it should be, and a page that has said it wants less movement gets none.
    //
    // Nor does a card the page merely moved around. Left to itself the spring
    // goes off whenever a render finds the card somewhere new, and it cannot
    // tell a rearrangement from the view settling into place: a report already
    // in the cache draws its cards on the first frame, while the page is still
    // taking its place, so it opened flush to the edge and slid in by the
    // width of the padding. ``layoutDependency`` names the one thing that is a
    // reason to travel.
    <motion.div
      ref={setNodeRef}
      data-report-card-travels
      data-report-card-id={id}
      layout={isStill ? "position" : false}
      layoutDependency={arrangement}
      transition={{ type: "spring", stiffness: 520, damping: 42, mass: 0.7 }}
      style={{
        ...gridStyle,
        position: "relative",
        minWidth: 0,
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "visible",
        ...(isDragging ? { zIndex: 2 } : {}),
      }}
    >
      <ReportsViewCard
        {...cardProps}
        assemble={assemble}
        className={cn(REPORT_CARD_SURFACE_CLASS, cardProps.className)}
        style={{ ...surfaceStyle, ...cardProps.style }}
      >
        {children}
      </ReportsViewCard>
      {gridPlacementEnabled ? (
        <>
          <ReportsViewWidgetResizeHandle
            axis="col"
            side="left"
            step={columnStep}
            cursor={resizeCursors?.left ?? null}
            onResize={(delta) => onResizeGrid("left", delta)}
            onResizeEnd={onResizeGridEnd}
          />
          <ReportsViewWidgetResizeHandle
            axis="col"
            side="right"
            step={columnStep}
            cursor={resizeCursors?.right ?? null}
            onResize={(delta) => onResizeGrid("right", delta)}
            onResizeEnd={onResizeGridEnd}
          />
          <ReportsViewWidgetResizeHandle
            axis="row"
            side="top"
            step={rowStep}
            cursor={resizeCursors?.top ?? null}
            onResize={(delta) => onResizeGrid("top", delta)}
            onResizeEnd={onResizeGridEnd}
          />
          <ReportsViewWidgetResizeHandle
            axis="row"
            side="bottom"
            step={rowStep}
            cursor={resizeCursors?.bottom ?? null}
            onResize={(delta) => onResizeGrid("bottom", delta)}
            onResizeEnd={onResizeGridEnd}
          />
        </>
      ) : null}
    </motion.div>
  );
}

/**
 * A card on the page, drawn again only when something about it changed - where
 * it is, what it shows, the ways its edges go. Everything it is given is data
 * compared by what it says, or a callback that stays the same for as long as
 * the page is up, so a card the page did not touch is left as it was.
 */
const ReportsViewCanvasPlacedCard = memo(function ReportsViewCanvasPlacedCard({
  card,
  widget,
  gridMode,
  order,
  gridPlacementEnabled,
  columnStep,
  rowStep,
  isResizing,
  resizeCursors,
  arrangement,
  reportIdentifier,
  layoutEntityIdentifiers,
  savedWidget,
  handleUpdateWidget,
  handleRemoveWidget,
  handleResizeGrid,
  handleResizeGridEnd,
}: {
  card: DashboardGridCard;
  widget: ReportLayoutWidget;
  gridMode: ReportsGridMode;
  /** Where it comes on the stacked page; the grid places it by its lines. */
  order?: number;
  gridPlacementEnabled: boolean;
  columnStep: number;
  rowStep: number;
  /** A pointer is on a resize handle somewhere on the page. */
  isResizing: boolean;
  resizeCursors?: ResizeCursors;
  /** Which arrangement of this card this is; it travels when it changes. */
  arrangement: number;
  reportIdentifier: string | null;
  layoutEntityIdentifiers: string[];
  savedWidget: ReportLayoutWidget | null;
  handleUpdateWidget: (
    widgetId: string,
    patch:
      | Partial<ReportLayoutWidget>
      | ((widget: ReportLayoutWidget) => Partial<ReportLayoutWidget>),
  ) => void;
  handleRemoveWidget: (widgetId: string) => void;
  handleResizeGrid: (
    cardId: string,
    side: "left" | "right" | "top" | "bottom",
    delta: number,
  ) => void;
  handleResizeGridEnd: () => void;
}) {
  const filterDefinitions = useReportsViewWidgetFilterDefinitions(widget);
  const { setWidgetFilters } = useReportsViewWidgetFilterOverrides();
  const persistFiltersTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const onHeaderFiltersChange = useCallback(
    (filters: FilterType[]) => {
      setWidgetFilters(card.cardId, filters);
      if (persistFiltersTimerRef.current) {
        clearTimeout(persistFiltersTimerRef.current);
      }
      persistFiltersTimerRef.current = setTimeout(() => {
        persistFiltersTimerRef.current = null;
        handleUpdateWidget(card.cardId, (currentWidget) => ({
          defaultFilters: mergeWidgetDefaultFilters(
            currentWidget,
            filters,
            filterDefinitions,
          ),
        }));
      }, 1500);
    },
    [card.cardId, filterDefinitions, handleUpdateWidget, setWidgetFilters],
  );
  useEffect(
    () => () => {
      if (persistFiltersTimerRef.current) {
        clearTimeout(persistFiltersTimerRef.current);
      }
    },
    [],
  );
  const headerFilters = useReportsViewWidgetHeaderFilters(
    widget,
    onHeaderFiltersChange,
  );
  const catalogue = useReportSourceCatalog();
  const cardGridStyle = { ...getCardGridStyle(card, gridMode), order };

  return (
    <ReportsViewSortableCard
      key={card.cardId}
      id={card.cardId}
      gridStyle={cardGridStyle}
      arrangement={arrangement}
      columnStep={columnStep}
      rowStep={rowStep}
      gridPlacementEnabled={gridPlacementEnabled}
      isResizing={isResizing}
      resizeCursors={resizeCursors}
      onResizeGrid={(side, delta) => handleResizeGrid(card.cardId, side, delta)}
      onResizeGridEnd={handleResizeGridEnd}
      cardProps={{
        // The sentence is the title, and it is also how the widget is
        // changed, so the card's own title row stands aside for it.
        title: null,
        // As tall as the squares it stands on: a resize moves its grid lines
        // and the card follows them in CSS, with nothing to render for it -
        // a chart inside measures its own box and redraws when that changes.
        height: "100%",
        headerFilters,
        contentFlush: widgetUsesFlushContent(widget),
        assemble: {
          titleNode: (
            <ReportsViewWidgetQuestion
              source={sourceByName(widget.source, catalogue)}
              sources={catalogue}
              spec={widget.spec}
              presentation={widget.presentation}
              onChange={(patch) =>
                handleUpdateWidget(card.cardId, (previous) =>
                  askedOfWidget(previous, patch, catalogue),
                )
              }
            />
          ),
          describeNode: (
            <ReportsViewWidgetPrompt
              widget={widget}
              sources={catalogue}
              onApply={(patch) =>
                handleUpdateWidget(card.cardId, (previous) => ({
                  ...previous,
                  ...patch,
                }))
              }
            />
          ),
        },
        onRemove: () => handleRemoveWidget(card.cardId),
      }}
    >
      <ReportsViewWidget
        widget={widget}
        reportIdentifier={reportIdentifier}
        layoutEntityIdentifiers={layoutEntityIdentifiers}
        savedWidget={savedWidget}
        onCompletePairing={handleUpdateWidget}
      />
    </ReportsViewSortableCard>
  );
}, propsSayTheSame);

function ReportsViewDragOverlayCard({
  size,
  children,
}: {
  size: { width: number; height: number } | null;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "opacity-[0.85] shadow-lg rounded-lg pointer-events-none min-w-0 overflow-hidden",
        size && "w-(--width) h-(--height)",
      )}
      style={
        {
          "--width": size
            ? size.width <= 1 && size.width !== 0
              ? `${size.width * 100}%`
              : `${size.width}px`
            : undefined,
          "--height": size
            ? size.height <= 1 && size.height !== 0
              ? `${size.height * 100}%`
              : `${size.height}px`
            : undefined,
        } as CSSProperties
      }
    >
      {children}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────

export function ReportsViewCanvas() {
  const cardSurfaceStyle = useReportCardSurfaceStyle();
  const filters = State((state) => state.filters);
  const setCurrentDashboardBreakpoint = State(
    (s) => s.setCurrentDashboardBreakpoint,
  );
  const {
    activeDefinition: apiDefinition,
    activeLayout,
    activeLayoutId,
    activeReportTabId,
    normalizedDefinition,
    saveDefinition,
    isReportPublic,
  } = useReportsViewReportLayoutContext();

  const { definition, updateDefinition, flushPendingDefinitionSave } =
    useReportDefinitionEditor({
      apiDefinition,
      normalizedDefinition,
      activeLayoutId,
      activeReportTabId,
      saveDefinition,
    });

  const savedWidgets = useMemo(
    () => persistedWidgets(apiDefinition),
    [apiDefinition],
  );

  const resizePreviewCardsRef = useRef<DashboardGridCard[] | null>(null);
  const resizingCardIdRef = useRef<string | null>(null);
  const dragPointerRef = useRef({ x: 0, y: 0 });
  // The page as it stood when a grid drag began, and the square of the card
  // the pointer holds it by: every frame of the drag is worked out afresh
  // from these, so where the card is drawn depends only on the pointer.
  const dragStartCardsRef = useRef<DashboardGridCard[] | null>(null);
  const dragGrabRef = useRef({ col: 0, row: 0 });
  /** Where the pointer holds the carried card, in pixels from its corner. */
  const dragHoldRef = useRef({ x: 0, y: 0 });
  const [draggingCardId, setDraggingCardId] = useState<string | null>(null);
  const layoutScopeKey = `${activeLayoutId ?? ""}:${activeReportTabId ?? ""}`;
  const layoutScopeKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (layoutScopeKeyRef.current !== layoutScopeKey) {
      layoutScopeKeyRef.current = layoutScopeKey;
      resizePreviewCardsRef.current = null;
      resizingCardIdRef.current = null;
      setResizePreviewCards(null);
    }
  }, [layoutScopeKey]);

  const layoutEntityIdentifiers = useMemo(
    () =>
      resolveLayoutEntityIdentifiers(
        (activeLayout?.entities ?? []).map((entity) => entity.identifier),
        filters,
      ),
    [activeLayout?.entities, filters],
  );

  // --- DnD ---

  const updateDashboardLayout = useCallback(
    (
      breakpoint: BreakpointKey,
      cards: ReportLayoutDefinition["layouts"][BreakpointKey]["cards"],
    ) => {
      updateDefinition((def) =>
        updateActiveReportGroupLayouts(
          def,
          activeReportTabId,
          breakpoint,
          cards,
        ),
      );
    },
    [activeReportTabId, updateDefinition],
  );

  const widgetsById = useMemo(() => {
    const map = new Map<string, ReportLayoutWidget>();
    for (const widget of definition?.widgets ?? []) {
      map.set(widget.id, widget);
    }
    return map;
  }, [definition?.widgets]);

  const [pendingCatalogWidget, setPendingCatalogWidget] =
    useState<ReportLayoutWidget | null>(null);

  const layoutWidgetsById = useMemo(() => {
    const map = new Map(widgetsById);
    if (pendingCatalogWidget) {
      map.set(pendingCatalogWidget.id, pendingCatalogWidget);
    }
    return map;
  }, [widgetsById, pendingCatalogWidget]);

  const [gridEl, setGridEl] = useState<HTMLDivElement | null>(null);
  const [gridWidth, setGridWidth] = useState(0);
  const gridMode = useReportsGridMode(gridEl);
  const layoutKey = reportsLayoutKeyForMode(gridMode);
  const columns = gridColumnCountForMode(gridMode);
  const gridPlacementEnabled = usesGridPlacementForMode(gridMode);
  const layoutColumns = gridColumnCountForMode(gridMode);
  const gridGapPx = Number.parseFloat("16px");
  const columnStep = useMemo(() => {
    if (layoutColumns <= 0 || gridWidth <= 0) return 0;
    const totalGap = Math.max(0, layoutColumns - 1) * gridGapPx;
    const columnWidth = (gridWidth - totalGap) / layoutColumns;
    return columnWidth + gridGapPx;
  }, [gridWidth, gridGapPx, layoutColumns]);
  const dashboardCardHeightPx = REPORTS_CARD_HEIGHT_PX;
  const rowStep = useMemo(() => {
    if (!gridPlacementEnabled || dashboardCardHeightPx <= 0) return 0;
    return dashboardCardHeightPx + gridGapPx;
  }, [dashboardCardHeightPx, gridGapPx, gridPlacementEnabled]);

  useEffect(() => {
    if (!gridEl) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setGridWidth(entry.contentRect.width);
      }
    });

    setGridWidth(gridEl.clientWidth);
    observer.observe(gridEl);

    return () => observer.disconnect();
  }, [gridEl]);

  useEffect(() => {
    setCurrentDashboardBreakpoint(layoutKey);
  }, [layoutKey, setCurrentDashboardBreakpoint]);

  const currentLayout = definition?.layouts[layoutKey];
  const [dragPreviewCards, setDragPreviewCards] = useState<
    typeof currentLayout.cards | null
  >(null);
  const [resizePreviewCards, setResizePreviewCards] = useState<
    typeof currentLayout.cards | null
  >(null);

  const gridDragActiveId = draggingCardId ?? pendingCatalogWidget?.id ?? null;

  // The carried card follows the pointer on the compositor: its place is
  // wherever the drag has put it - its squares in the grid, its slot on the
  // stacked page - and ``translate`` takes it from there to the pointer, held
  // where it was picked up. Written straight to the element, as the pointer
  // moves, so following it is nothing React has to render; and it is the card
  // itself, not a copy put up over a gap, so nothing on it is drawn afresh -
  // a copy's figures counted up from nothing the moment it was picked up.
  const carryCard = useCallback(() => {
    if (!gridEl || !draggingCardId) return;
    const card = gridEl.querySelector<HTMLElement>(
      `[data-report-card-id="${CSS.escape(draggingCardId)}"]`,
    );
    if (!card) return;
    card.style.translate = "";
    const square = card.getBoundingClientRect();
    const { x, y } = dragPointerRef.current;
    const hold = dragHoldRef.current;
    card.style.translate = `${x - hold.x - square.left}px ${y - hold.y - square.top}px`;
  }, [draggingCardId, gridEl]);

  // Carried again whenever the drag moves its squares, in the same frame; and
  // put down, where it now stands, when the drag ends.
  useLayoutEffect(() => {
    carryCard();
  }, [carryCard, dragPreviewCards]);
  useLayoutEffect(() => {
    if (!gridEl || !draggingCardId) return;
    const card = gridEl.querySelector<HTMLElement>(
      `[data-report-card-id="${CSS.escape(draggingCardId)}"]`,
    );
    return () => {
      if (card) card.style.translate = "";
    };
  }, [draggingCardId, gridEl]);

  useEffect(() => {
    if (!gridDragActiveId) return;

    let rafId: number | null = null;

    const applyGridDragReorder = () => {
      if (!gridEl || !gridPlacementEnabled) return;

      const startCards = dragStartCardsRef.current;
      if (!startCards) return;

      const rect = gridEl.getBoundingClientRect();
      const { x, y } = dragPointerRef.current;
      const { col, row } = gridCellFromPointer(
        rect,
        x,
        y,
        columns,
        dashboardCardHeightPx,
        gridGapPx,
      );
      const next = dragCardToGridCell(
        startCards,
        gridDragActiveId,
        col,
        row,
        dragGrabRef.current,
        columns,
      );
      if (!next) return;
      const placementsOf = (cards: DashboardGridCard[]) =>
        cards
          .map((card) =>
            [card.cardId, card.colStart, card.colEnd, card.rowStart].join(":"),
          )
          .join("|");
      setDragPreviewCards((prev) =>
        prev && placementsOf(prev) === placementsOf(next) ? prev : next,
      );
    };

    const onPointerMove = (event: PointerEvent) => {
      dragPointerRef.current = { x: event.clientX, y: event.clientY };
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        applyGridDragReorder();
        carryCard();
      });
    };

    window.addEventListener("pointermove", onPointerMove);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [
    carryCard,
    columns,
    dashboardCardHeightPx,
    gridDragActiveId,
    gridEl,
    gridGapPx,
    gridPlacementEnabled,
    layoutWidgetsById,
  ]);

  const effectiveCards = useMemo(() => {
    // A widget being dragged into a new order wins over one being resized:
    // a resize whose release was never heard would otherwise sit over the
    // page and make dragging look broken.
    const previewCards = dragPreviewCards ?? resizePreviewCards;
    const cards = previewCards ?? currentLayout?.cards ?? [];
    if (!dragPreviewCards && resizePreviewCards && gridPlacementEnabled) {
      // Drawn as it will be saved. It used to be packed afresh for the
      // preview and saved as the boundary left it, so the page rearranged
      // itself under the pointer and again on release.
      return resizePreviewCards;
    }
    if (dragPreviewCards && gridPlacementEnabled) {
      // Already a layout: worked out from the pointer, and saved as drawn.
      return dragPreviewCards;
    }
    return fitCardsToGrid(cards, layoutWidgetsById, gridMode);
  }, [
    columns,
    dragPreviewCards,
    resizePreviewCards,
    currentLayout?.cards,
    gridMode,
    gridPlacementEnabled,
    layoutWidgetsById,
  ]);
  const visibleCards = useMemo(
    () => effectiveCards.filter((c) => c.visible),
    [effectiveCards],
  );

  // Which ways each edge of each card can go, asked of the resize itself: an
  // edge shows the arrow it can be dragged in - one way when it can only grow
  // or only shrink, both ways when it can do either - and no handle at all
  // when it can do neither.
  const resizeCursors = useMemo(() => {
    const cursors = new Map<string, ResizeCursors>();
    if (!gridPlacementEnabled || gridDragActiveId != null) return cursors;
    const arrows = {
      right: ["e-resize", "w-resize", "ew-resize"],
      left: ["w-resize", "e-resize", "ew-resize"],
      bottom: ["s-resize", "n-resize", "ns-resize"],
      top: ["n-resize", "s-resize", "ns-resize"],
    } as const;
    for (const card of visibleCards) {
      const edges: ResizeCursors = {};
      for (const side of ["left", "right", "top", "bottom"] as const) {
        const outward = side === "right" || side === "bottom" ? 1 : -1;
        const can = (delta: number) =>
          resizeCardsInGrid(
            effectiveCards,
            card.cardId,
            side,
            delta,
            layoutWidgetsById,
            columns,
          ) != null;
        const [grow, shrink, both] = arrows[side];
        const grows = can(outward);
        const shrinks = can(-outward);
        edges[side] =
          grows && shrinks ? both : grows ? grow : shrinks ? shrink : null;
      }
      cursors.set(card.cardId, edges);
    }
    return cursors;
  }, [
    columns,
    effectiveCards,
    gridPlacementEnabled,
    gridDragActiveId,
    layoutWidgetsById,
    visibleCards,
  ]);

  // How many times the cards have been rearranged: moved, resized, added or
  // taken away. It is what lets a card travel to its new square, so it does
  // not count the page changing between its wide and stacked modes - that is
  // the same arrangement read at another width, and it happens on the way in,
  // as the grid measures itself for the first time.
  const placements = visibleCards
    .map((card) =>
      [
        card.cardId,
        card.colSpan,
        card.colStart,
        card.colEnd,
        card.rowStart,
        card.rowEnd,
      ].join(":"),
    )
    .join("|");
  // Counted card by card: a card travels when it is the one rearranged, and a
  // card left where it was is not drawn again because another one moved.
  const [arranged, setArranged] = useState<{
    gridMode: ReportsGridMode;
    byCard: Record<string, { placement: string; revision: number }>;
  }>({ gridMode, byCard: {} });
  const cardPlacements = placements.split("|");
  const nextByCard = Object.fromEntries(
    cardPlacements.map((placement) => {
      const cardId = placement.split(":")[0];
      const was = arranged.byCard[cardId];
      const moved =
        was != null &&
        was.placement !== placement &&
        arranged.gridMode === gridMode;
      return [
        cardId,
        {
          placement,
          revision: (was?.revision ?? 0) + (moved ? 1 : 0),
        },
      ];
    }),
  );
  if (
    arranged.gridMode !== gridMode ||
    cardPlacements.some(
      (placement) =>
        arranged.byCard[placement.split(":")[0]]?.placement !== placement,
    )
  ) {
    setArranged({ gridMode, byCard: nextByCard });
  }

  const [dragSize, setDragSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  const { setNodeRef: setCanvasDropRef } = useDroppable({
    id: DASHBOARD_CANVAS_DROP_ID,
  });

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const activeId = String(event.active.id);
      setDraggingCardId(activeId);
      const activator = event.activatorEvent;
      if (
        activator &&
        "clientX" in activator &&
        "clientY" in activator &&
        typeof activator.clientX === "number" &&
        typeof activator.clientY === "number"
      ) {
        dragPointerRef.current = {
          x: activator.clientX,
          y: activator.clientY,
        };
      }
      // Any resize that never heard its release ends here.
      resizePreviewCardsRef.current = null;
      resizingCardIdRef.current = null;
      setResizePreviewCards(null);
      const startCards = sortCardsByGridPosition([
        ...(currentLayout?.cards ?? []),
      ]);
      // Measured off the card itself: dnd-kit has not measured it yet when the
      // drag starts, and a card held by nothing jumped to put its corner under
      // the pointer.
      const held = gridEl
        ?.querySelector(`[data-report-card-id="${CSS.escape(activeId)}"]`)
        ?.getBoundingClientRect();
      dragHoldRef.current = held
        ? {
            x: dragPointerRef.current.x - held.left,
            y: dragPointerRef.current.y - held.top,
          }
        : { x: 0, y: 0 };
      if (gridPlacementEnabled) {
        const placed = ensureGridPlacement(
          startCards,
          layoutWidgetsById,
          columns,
        );
        dragStartCardsRef.current = placed;
        const card = placed.find((c) => c.cardId === activeId);
        if (gridEl && card?.colStart != null && card.rowStart != null) {
          const { col, row } = gridCellFromPointer(
            gridEl.getBoundingClientRect(),
            dragPointerRef.current.x,
            dragPointerRef.current.y,
            columns,
            dashboardCardHeightPx,
            gridGapPx,
          );
          dragGrabRef.current = {
            col: col - card.colStart,
            row: row - card.rowStart,
          };
        }
        setDragPreviewCards(placed);
      } else {
        setDragPreviewCards(startCards);
      }
      const el = event.active.rect.current.initial;
      if (el) {
        setDragSize({ width: el.width, height: el.height });
      }
    },
    [
      columns,
      currentLayout,
      dashboardCardHeightPx,
      gridEl,
      gridGapPx,
      gridPlacementEnabled,
      layoutWidgetsById,
    ],
  );

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      if (gridPlacementEnabled) return;

      const activeId = String(event.active.id);

      setDragPreviewCards((prev) => {
        if (!prev) return prev;

        const { over } = event;
        if (!over || over.id === activeId) return prev;

        const overId = String(over.id);
        if (overId === DASHBOARD_CANVAS_DROP_ID) {
          const activeCard = prev.find((card) => card.cardId === activeId);
          if (!activeCard) return prev;
          const without = prev.filter((card) => card.cardId !== activeId);
          const next = [...without, activeCard];
          return sameDashboardCardOrder(prev, next) ? prev : next;
        }

        const oldIndex = prev.findIndex((card) => card.cardId === activeId);
        const newIndex = prev.findIndex((card) => card.cardId === overId);
        if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
          return prev;
        }
        return arrayMove(prev, oldIndex, newIndex);
      });
    },
    [gridPlacementEnabled],
  );

  const handleDragEnd = useCallback(
    (_event: DragEndEvent) => {
      if (dragPreviewCards) {
        updateDashboardLayout(layoutKey, dragPreviewCards);
        queueMicrotask(flushPendingDefinitionSave);
      }
      dragStartCardsRef.current = null;
      setDragSize(null);
      setDragPreviewCards(null);
      setDraggingCardId(null);
    },
    [
      layoutKey,
      dragPreviewCards,
      flushPendingDefinitionSave,
      updateDashboardLayout,
    ],
  );

  const handleDragCancel = useCallback(() => {
    dragStartCardsRef.current = null;
    setDragSize(null);
    setDragPreviewCards(null);
    setDraggingCardId(null);
  }, []);

  const clearCatalogDrag = useCallback(() => {
    dragStartCardsRef.current = null;
    setPendingCatalogWidget(null);
    setDragPreviewCards(null);
    setDragSize(null);
  }, []);

  const handleAssemblyDragStart = useCallback(
    ({ widget }: ReportsViewAssemblyDragSession) => {
      const { defaultColSpan } = getWidgetColSpanConstraints(widget, columns);
      setPendingCatalogWidget(widget);
      setDragSize({ width: 320, height: 180 });
      const startCards = sortCardsByGridPosition([
        ...(currentLayout?.cards ?? []),
        { cardId: widget.id, colSpan: defaultColSpan, visible: true },
      ]);
      if (gridPlacementEnabled) {
        // A new card has no square yet: it starts below the page, and is held
        // by its corner.
        const placed = ensureGridPlacement(
          startCards,
          new Map(widgetsById).set(widget.id, widget),
          columns,
        );
        dragStartCardsRef.current = placed;
        dragGrabRef.current = { col: 0, row: 0 };
        setDragPreviewCards(placed);
      } else {
        setDragPreviewCards(startCards);
      }
    },
    [columns, currentLayout?.cards, gridPlacementEnabled, widgetsById],
  );

  const handleAssemblyDragOver = useCallback(
    (event: DragOverEvent) => {
      if (!pendingCatalogWidget || gridPlacementEnabled) return;
      const { defaultColSpan } = getWidgetColSpanConstraints(
        pendingCatalogWidget,
        columns,
      );
      const overId = event.over?.id ? String(event.over.id) : null;
      if (overId && isCatalogDragId(overId)) return;

      setDragPreviewCards((prev) => {
        if (!prev) return prev;
        const next = reorderGhostCard(
          prev,
          pendingCatalogWidget.id,
          overId,
          defaultColSpan,
          DASHBOARD_CANVAS_DROP_ID,
        );
        return sameDashboardCardOrder(prev, next) ? prev : next;
      });
    },
    [columns, gridPlacementEnabled, pendingCatalogWidget],
  );

  const handleAssemblyDragCancel = useCallback(() => {
    clearCatalogDrag();
  }, [clearCatalogDrag]);

  const handleToggleVisibility = useCallback(
    (cardId: string) => {
      const allCards = effectiveCards.map((c) =>
        c.cardId === cardId ? { ...c, visible: !c.visible } : c,
      );
      updateDashboardLayout(layoutKey, allCards);
    },
    [effectiveCards, layoutKey, updateDashboardLayout],
  );

  const handleResizeGrid = useCallback(
    (
      cardId: string,
      side: "left" | "right" | "top" | "bottom",
      delta: number,
    ) => {
      if (!delta || !gridPlacementEnabled) return;

      setResizePreviewCards((prev) => {
        const cards = prev ?? currentLayout?.cards ?? [];
        // The whole page gives way around the drag: the widget across the
        // boundary gives up what this one takes, and what is underneath is
        // pushed down. What comes back is already a layout, so it is what
        // the card is drawn from and what is saved when the drag ends.
        const nextCards = resizeCardsInGrid(
          cards,
          cardId,
          side,
          delta,
          layoutWidgetsById,
          columns,
        );
        if (!nextCards) return prev;

        resizingCardIdRef.current = cardId;
        resizePreviewCardsRef.current = nextCards;
        return nextCards;
      });
    },
    [columns, currentLayout?.cards, gridPlacementEnabled, layoutWidgetsById],
  );

  const handleResizeGridEnd = useCallback(() => {
    const cards = resizePreviewCardsRef.current;
    if (!cards) return;
    resizePreviewCardsRef.current = null;
    resizingCardIdRef.current = null;
    setResizePreviewCards(null);
    // Saved as it was drawn. It used to be re-flowed here instead - every
    // widget but the one dragged packed afresh into the first space it fit -
    // so the page rearranged itself on release, and the arrangement could not
    // be got back by dragging the edge the other way.
    updateDashboardLayout(layoutKey, cards);
    queueMicrotask(flushPendingDefinitionSave);
  }, [
    columns,
    flushPendingDefinitionSave,
    gridPlacementEnabled,
    layoutKey,
    layoutWidgetsById,
    updateDashboardLayout,
  ]);

  const handleAddWidget = useCallback(
    (
      widget: ReportLayoutWidget,
      defaultColSpan?: number,
      orderedCards?: DashboardGridCard[],
    ) => {
      const span =
        defaultColSpan ??
        getWidgetColSpanConstraints(widget, columns).defaultColSpan;

      updateDefinition((def) => {
        let next = addWidgetToDefinition(def, widget, span, activeReportTabId);

        if (orderedCards) {
          const tabScoped = definitionForActiveReportGroup(
            next,
            activeReportTabId,
          );
          const hidden = tabScoped.layouts[layoutKey].cards.filter(
            (c) => !c.visible,
          );
          const widgets = new Map(next.widgets.map((w) => [w.id, w]));
          const visibleOrdered = orderedCards
            .filter((c) => c.visible)
            .map((c) => ({
              ...c,
              colSpan:
                c.cardId === widget.id
                  ? span
                  : clampCardColSpan(c.colSpan, widgets.get(c.cardId), columns),
            }));
          const placed = gridPlacementEnabled
            ? ensureGridPlacement(visibleOrdered, widgets, columns)
            : visibleOrdered;
          const merged: DashboardGridCard[] = [...placed, ...hidden];
          next = updateActiveReportGroupLayouts(
            next,
            activeReportTabId,
            layoutKey,
            merged,
          );
        }

        return definitionForActiveReportGroup(next, activeReportTabId);
      });
      queueMicrotask(flushPendingDefinitionSave);
    },
    [
      activeReportTabId,
      columns,
      layoutKey,
      gridPlacementEnabled,
      updateDefinition,
      flushPendingDefinitionSave,
    ],
  );

  const handleAssemblyDragEnd = useCallback(
    (event: DragEndEvent) => {
      const overId = event.over?.id ? String(event.over.id) : null;
      const pointerOverGrid = (() => {
        if (!gridEl) return false;
        const rect = gridEl.getBoundingClientRect();
        const { x, y } = dragPointerRef.current;
        return (
          x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom
        );
      })();
      const droppedOnTarget =
        overId === DASHBOARD_CANVAS_DROP_ID ||
        (overId != null && !isCatalogDragId(overId)) ||
        (gridPlacementEnabled && pointerOverGrid);

      if (droppedOnTarget && pendingCatalogWidget && dragPreviewCards) {
        const defaultColSpan = getWidgetColSpanConstraints(
          pendingCatalogWidget,
          columns,
        ).defaultColSpan;
        handleAddWidget(pendingCatalogWidget, defaultColSpan, dragPreviewCards);
      }

      clearCatalogDrag();
    },
    [
      columns,
      clearCatalogDrag,
      dragPreviewCards,
      gridEl,
      gridPlacementEnabled,
      handleAddWidget,
      pendingCatalogWidget,
    ],
  );

  const handleUpdateWidget = useCallback(
    (
      widgetId: string,
      patch:
        | Partial<ReportLayoutWidget>
        | ((widget: ReportLayoutWidget) => Partial<ReportLayoutWidget>),
    ) => {
      updateDefinition((def) => {
        const widget = def.widgets.find((w) => w.id === widgetId);
        if (!widget) return def;
        const resolvedPatch =
          typeof patch === "function" ? patch(widget) : patch;
        return updateWidgetInDefinition(def, widgetId, resolvedPatch);
      });
      queueMicrotask(flushPendingDefinitionSave);
    },
    [flushPendingDefinitionSave, updateDefinition],
  );

  const handleRemoveWidget = useCallback(
    (widgetId: string) => {
      updateDefinition((def) => removeWidgetFromDefinition(def, widgetId));
    },
    [updateDefinition],
  );

  // What the cards call, the same callbacks for as long as the canvas is up,
  // each doing whatever the latest of it does: new ones each time the report
  // is saved or a drag moves on would be a reason to draw every card again.
  const cardCallbacksRef = useRef({
    handleUpdateWidget,
    handleRemoveWidget,
    handleResizeGrid,
    handleResizeGridEnd,
  });
  cardCallbacksRef.current = {
    handleUpdateWidget,
    handleRemoveWidget,
    handleResizeGrid,
    handleResizeGridEnd,
  };
  const cardCallbacks = useMemo(
    () => ({
      handleUpdateWidget: (...args: Parameters<typeof handleUpdateWidget>) =>
        cardCallbacksRef.current.handleUpdateWidget(...args),
      handleRemoveWidget: (widgetId: string) =>
        cardCallbacksRef.current.handleRemoveWidget(widgetId),
      handleResizeGrid: (...args: Parameters<typeof handleResizeGrid>) =>
        cardCallbacksRef.current.handleResizeGrid(...args),
      handleResizeGridEnd: () => cardCallbacksRef.current.handleResizeGridEnd(),
    }),
    [],
  );

  useReportsViewAssembleDndRegistration({
    onAssemblyDragStart: handleAssemblyDragStart,
    onAssemblyDragOver: handleAssemblyDragOver,
    onAssemblyDragEnd: handleAssemblyDragEnd,
    onAssemblyDragCancel: handleAssemblyDragCancel,
    onSortableDragStart: handleDragStart,
    onSortableDragOver: handleDragOver,
    onSortableDragEnd: handleDragEnd,
    onSortableDragCancel: handleDragCancel,
    // Tapped rather than dragged: to the end of the report, where a drop with
    // nowhere in particular to go would put it.
    onAssemblyAdd: (widget) => handleAddWidget(widget),
    renderAssemblyOverlay: ({ widget }) => (
      <ReportsViewDragOverlayCard size={dragSize}>
        <ReportsViewCard
          title={widgetDisplayTitle(widget)}
          className={REPORT_CARD_SURFACE_CLASS}
          style={cardSurfaceStyle}
        >
          <ReportsViewWidget
            widget={widget}
            reportIdentifier={activeLayoutId}
            layoutEntityIdentifiers={layoutEntityIdentifiers}
            isLoading
          />
        </ReportsViewCard>
      </ReportsViewDragOverlayCard>
    ),
  });

  const setGridRef = useCallback(
    (node: HTMLDivElement | null) => {
      setGridEl(node);
      setCanvasDropRef(node);
    },
    [setCanvasDropRef],
  );

  // --- Hidden cards panel (edit mode) ---

  const hiddenCards = useMemo(
    () => effectiveCards.filter((c) => !c.visible),
    [effectiveCards],
  );

  // --- Render ---

  // The report opens as four even cards, so its loading state is those same
  // four cards in the same grid — two rows of two, no padding around them.
  if (!definition) {
    return (
      <div
        className="grid gap-4 w-full grid-cols-(--grid-template-columns-2) auto-rows-[400px] rounded-md"
        style={
          {
            "--grid-template-columns-2": `repeat(${layoutColumns}, minmax(0, 1fr))`,
          } as CSSProperties
        }
      >
        {[0, 1, 2, 3].map((index) => (
          <Skeleton
            key={index}
            variant="rectangular"
            className="[grid-column:var(--grid-column)] h-full rounded-md"
            style={
              {
                "--grid-column": `span ${Math.max(1, Math.round(layoutColumns / 2))}`,
              } as CSSProperties
            }
          />
        ))}
      </div>
    );
  }

  return (
    <ReportsViewWidgetFilterOverridesProvider resetKey={layoutScopeKey}>
      <div
        ref={setGridRef}
        className="grid gap-4 w-full grid-cols-(--grid-template-columns-2) auto-rows-[400px] rounded-md"
        style={
          {
            "--grid-template-columns-2": `repeat(${layoutColumns}, minmax(0, 1fr))`,
          } as CSSProperties
        }
      >
        {/* Drawn in one order that never changes, whatever order the
                cards are in: where a card stands is its grid lines, or on the
                stacked page its ``order``, not where its element is among the
                others. Drawn in the order they are listed, a drag that put the
                list in reading order moved the cards' elements about, and
                React - which in development runs a moved component's effects
                again to prove it can - started every card's charts and figures
                over: the figures counting up from nothing on each drag. */}
        {[...visibleCards]
          .sort((a, b) => a.cardId.localeCompare(b.cardId))
          .map((card) => {
            const widget = layoutWidgetsById.get(card.cardId);
            const isPending = card.cardId === pendingCatalogWidget?.id;
            const order = gridPlacementEnabled
              ? undefined
              : visibleCards.indexOf(card);
            const cardGridStyle = {
              ...getCardGridStyle(card, gridMode),
              order,
            };

            if (isPending) {
              return (
                <div
                  key={card.cardId}
                  style={{
                    ...cardGridStyle,
                    position: "relative",
                    minWidth: 0,
                    width: "100%",
                    height: "100%",
                    pointerEvents: "none",
                  }}
                >
                  <ReportsViewDragPlaceholder height={dragSize?.height} />
                </div>
              );
            }

            if (!widget) {
              return null;
            }

            return (
              <ReportsViewCanvasPlacedCard
                key={card.cardId}
                card={card}
                widget={widget}
                gridMode={gridMode}
                order={order}
                gridPlacementEnabled={gridPlacementEnabled}
                columnStep={columnStep}
                rowStep={rowStep}
                isResizing={resizePreviewCards != null}
                resizeCursors={resizeCursors.get(card.cardId)}
                arrangement={arranged.byCard[card.cardId]?.revision ?? 0}
                reportIdentifier={activeLayoutId}
                layoutEntityIdentifiers={layoutEntityIdentifiers}
                savedWidget={savedWidgets.get(card.cardId) ?? null}
                {...cardCallbacks}
              />
            );
          })}

        {hiddenCards.length > 0 && (
          <div className="col-span-full p-3 rounded-md [border-width:1px] [border-style:dashed] border-border">
            <Typography
              level="body-xs"
              className="text-muted-foreground/80 font-semibold uppercase [letter-spacing:0.05em] mb-2"
            >
              Hidden components
            </Typography>
            <div className="flex gap-2 flex-wrap">
              {hiddenCards.map((card) => {
                const widget = widgetsById.get(card.cardId);
                return (
                  <div
                    key={card.cardId}
                    onClick={() => handleToggleVisibility(card.cardId)}
                    className="pl-3 pr-3 pt-1.5 pb-1.5 rounded-sm [border-width:1px] [border-style:solid] border-border bg-card cursor-pointer hover:bg-muted"
                  >
                    <Typography level="body-xs" className="font-semibold">
                      {widget ? widgetDisplayTitle(widget) : card.cardId}
                    </Typography>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </ReportsViewWidgetFilterOverridesProvider>
  );
}
